import React from "react";
import {
  Bot,
  Workflow,
  Building2,
  FlaskConical,
  Globe,
  Cloud,
  ShoppingCart,
  Factory,
  Shuffle,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const ICONS = {
  lab: FlaskConical,
  bot: Bot,
  globe: Globe,
  cloud: Cloud,
  cart: ShoppingCart,
  factory: Factory,
  workflow: Workflow,
  building: Building2,
  router: Shuffle,
};

const Services = () => {
  const { t } = useLang();
  return (
    <section id="services" data-testid="services-section" className="py-16 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="eyebrow">{t.services.eyebrow}</div>
            <h2 className="mt-4 font-display font-light text-4xl md:text-5xl tracking-tight text-maif max-w-2xl">
              {t.services.title}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.services.items.map((it, i) => {
            const Icon = ICONS[it.icon] || Bot;
            const isFlagship = i === 0;
            return (
              <article
                key={i}
                data-testid={`service-card-${i}`}
                className={`card-surface p-8 md:p-10 group relative overflow-hidden ${
                  isFlagship ? "lg:col-span-2 lg:row-span-1" : ""
                }`}
                style={
                  isFlagship
                    ? {
                        background:
                          "linear-gradient(140deg, #1A1A1E 0%, #111114 60%)",
                        borderColor: "rgba(212,111,77,0.25)",
                      }
                    : undefined
                }
              >
                {isFlagship && (
                  <div
                    aria-hidden
                    className="absolute -top-24 -right-24 w-72 h-72 rounded-full pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(closest-side, rgba(212,111,77,0.25), rgba(212,111,77,0) 70%)",
                    }}
                  />
                )}
                <div className="flex items-start justify-between relative">
                  <span className="font-accent italic text-xl text-terracotta/70">
                    {it.tag}
                  </span>
                  <Icon
                    size={24}
                    className="text-sand opacity-70 group-hover:opacity-100 transition"
                  />
                </div>
                <div className="mt-8 flex items-center gap-3">
                  <h3 className="font-display text-2xl md:text-3xl text-maif tracking-tight">
                    {it.title}
                  </h3>
                  {it.status && (
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] font-semibold border ${
                        it.status === "Live"
                          ? "border-emerald-400/30 text-emerald-300 bg-emerald-400/5"
                          : "border-sand-soft text-maif-muted bg-white/[0.02]"
                      }`}
                    >
                      {it.status}
                    </span>
                  )}
                </div>
                <p className="mt-3 text-maif-muted text-sm md:text-base max-w-lg">
                  {it.desc}
                </p>
                <ul className="mt-6 flex flex-wrap gap-2">
                  {it.bullets.map((b, k) => (
                    <li
                      key={k}
                      className="inline-flex items-center rounded-full border border-sand-soft bg-white/[0.02] px-2.5 py-1 text-[11px] text-maif-muted"
                    >
                      {b}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
